import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Form, Col } from 'react-bootstrap';
import Button from '../../../../components/Button';
import NumberFormat from 'react-number-format';
import If from '../../../../components/If';
import AlertError from '../../../../components/AlertError';
import AlertSuccess from '../../../../components/AlertSuccess';
import ResponseError from '../../../../components/ResponseError';
import { STRINGS } from '../../../../util/strings';
import { BreederForm } from '../../../../models/models';
import api from '../../../../services/api';
import '../../styles_add.css';

export default function Breeder ()
{
	const history = useHistory();
	const [successfulResponse, setSuccessfulResponse] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<any>(null);
	const [errorMsg, setErrorMsg] = useState<string>('');

	const breederSchema = yup.object().shape({
		name: yup.string().required(STRINGS.FORM_FIELD_REQUIRED),
		cpf: yup.string().required(STRINGS.FORM_FIELD_REQUIRED),
		registrationClassOrgan: yup.string(),
		professionalAddress: yup.string().required(STRINGS.FORM_FIELD_REQUIRED),
		phone: yup.string().required(STRINGS.FORM_FIELD_REQUIRED),
		email: yup.string().email(STRINGS.FORM_EMAIL_INVALID).required(STRINGS.FORM_FIELD_REQUIRED),
		titration: yup.string().required(STRINGS.FORM_FIELD_REQUIRED),
		cvLattes: yup.string(),
		mainTechnician: yup.boolean(),
		active: yup.boolean()
	});

	const initialValues: BreederForm = {
		name: '',
		cpf: '',
		registrationClassOrgan: '',
		professionalAddress: '',
		phone: '',
		email: '',
		titration: '',
		cvLattes: '',
		mainTechnician: false,
		active: true
	}

	const { register, errors, handleSubmit, reset } = useForm({
		defaultValues: initialValues,
		mode: 'onTouched',
		resolver: yupResolver(breederSchema)
	})

	const verifyErrorsOnForm = (): boolean => {
		if (errors.name) {
			return true;
		}
		return false;
	}

	const onSubmit = async (breederData: BreederForm) =>
	{
		setErrorMsg('');
		setLoading(true);
		setError(null);
		
		console.log('On Submit');

		try
		{
			let response = await api.post('api/v1/cultivares/EquipeTecnicaMelhorista', {
				nome: breederData.name,
				cpf: breederData.cpf,
				registroOrgaoClasse: breederData.registrationClassOrgan,
				enderecoProfissional: breederData.professionalAddress,
				telefone: breederData.phone,
				email: breederData.email,
				titulacao: breederData.titration,
				cvLattes: breederData.cvLattes,
				tecnicoPrincipal: breederData.mainTechnician,
				ativo: breederData.active
			});

			if (response.status !== 201)
			{
				setErrorMsg(response?.data?.message);
				throw Error(response?.data?.message);
			}

			setSuccessfulResponse(true);
			reset();
			setTimeout(() => {
				history.goBack();
			}, 3000);
		}
		catch (error:any)
		{
			console.error(error?.response?.data);
			setErrorMsg(error?.response?.data?.message);
		}

		setLoading(false);
	}

	return <div className="container container-fluid">
		<If condition={verifyErrorsOnForm()}>
			<AlertError
				message={STRINGS.ALERT_WARNING_FIELDS}
			/>
		</If>
		<If condition={successfulResponse && !error}>
			<AlertSuccess
				message={STRINGS.ALERT_MESSAGE_SUCCESS}
			/>
		</If>
		<If condition={!successfulResponse && error !== null}>
			{error !== null && error.message.length > 0 ? error.message.map((errorE: string) => <AlertError message={errorE} />) : <AlertError message={STRINGS.ALERT_GENERIC_ERROR} />}
		</If>
		<If condition={errorMsg !== ''}>
			<AlertError message={errorMsg} />
		</If>
		<h3>Cadastro de melhoristas</h3>
		<Form onSubmit={handleSubmit(onSubmit)}>
			<Form.Row className="row-custom">
				<Form.Group as={Col} md={5}>
					<Form.Label>
						Nome do&#40;a&#41; melhorista<em>*</em>
					</Form.Label>
					<Form.Control
						ref={register}
						name="name"
						type="text"
						placeholder='Insira o nome do&#40;a&#41; melhorista'
						isInvalid={errors.name !== undefined}
						autoComplete="off"
						maxLength={100}
					/>
					<If condition={errors.name}>
						<Form.Control.Feedback type="invalid">
							{errors.name?.message}
						</Form.Control.Feedback>
					</If>
				</Form.Group>
				<Form.Group as={Col} md={4}>
					<Form.Label>
						CPF<em>*</em>
					</Form.Label>
					<Form.Control
						ref={register}
						as={NumberFormat}
						getInputRef={register}
						type="text"
						placeholder='Insira o cpf do&#40;a&#41; melhorista'
						isInvalid={errors.cpf !== undefined}
						name="cpf"
						format="###.###.###-##"
						autoComplete="off"
					/>
					<If condition={errors.cpf}>
						<Form.Control.Feedback type="invalid">
							{errors.cpf?.message}
						</Form.Control.Feedback>
					</If>
				</Form.Group>
				<Form.Group as={Col} md={3}>
					<Form.Label>
						Telefone<em>*</em>
					</Form.Label>
					<Form.Control
						ref={register}
						as={NumberFormat}
						getInputRef={register}
						format="(##) #####-####"
						name="phone"
						type="text"
						placeholder='Insira o telefone do&#40;a&#41; melhorista'
						isInvalid={errors.phone !== undefined}
						autoComplete="off"
					/>
					<If condition={errors.phone}>
						<Form.Control.Feedback type="invalid">
							{errors.phone?.message}
						</Form.Control.Feedback>
					</If>
				</Form.Group>
			</Form.Row>
			<Form.Row className="row-custom">
				<Form.Group as={Col}>
					<Form.Label>
						Registro no órgão de classe &#40;CREA/CTNBio&#41;
					</Form.Label>
					<Form.Control
						ref={register}
						name="registrationClassOrgan"
						type="text"
						placeholder='Insira o registro no órgão de classe do&#40;a&#41; melhorista'
						autoComplete="off"
						maxLength={50}
					/>
					<If condition={errors.registrationClassOrgan}>
						<Form.Control.Feedback type="invalid">
							{errors.registrationClassOrgan?.message}
						</Form.Control.Feedback>
					</If>
				</Form.Group>
				<Form.Group as={Col}>
					<Form.Label>
						Email<em>*</em>
					</Form.Label>
					<Form.Control
						ref={register}
						name="email"
						type="email"
						placeholder='Insira o email do&#40;a&#41; melhorista'
						autoComplete="off"
						isInvalid={errors.email !== undefined}
						maxLength={150}
					/>
					<If condition={errors.email}>
						<Form.Control.Feedback type="invalid">
							{errors.email?.message}
						</Form.Control.Feedback>
					</If>
				</Form.Group>
			</Form.Row>
			<Form.Row className="row-custom">
				<Form.Group as={Col}>
					<Form.Label>
						Endereço profissional<em>*</em>
					</Form.Label>
					<Form.Control
						ref={register}
						name="professionalAddress"
						type="text"
						placeholder='Insira o endereço profissional do&#40;a&#41; melhorista'
						autoComplete="off"
						isInvalid={errors.professionalAddress !== undefined}
						maxLength={400}
					/>
					<If condition={errors.professionalAddress}>
						<Form.Control.Feedback type="invalid">
							{errors.professionalAddress?.message}
						</Form.Control.Feedback>
					</If>
				</Form.Group>
			</Form.Row>
			<Form.Row className="row-custom">
				<Form.Group as={Col}>
					<Form.Label>
						Titulação<em>*</em>
					</Form.Label>
					<Form.Control
						ref={register}
						name="titration"
						placeholder='Insira a titulação do&#40;a&#41; melhorista'
						isInvalid={errors.titration !== undefined}
						autoComplete="off"
						maxLength={200}
					/>
					<If condition={errors.titration != null}>
						<Form.Control.Feedback type="invalid">
							{errors.titration?.message}
						</Form.Control.Feedback>
					</If>
				</Form.Group>
				<Form.Group as={Col}>
					<Form.Label>
						CV Lattes
					</Form.Label>
					<Form.Control
						ref={register}
						name="cvLattes"
						placeholder='Insira a url do CV Lattes do&#40;a&#41; melhorista'
						autoComplete="off"
						maxLength={150}
					/>
					<If condition={errors.cvLattes}>
						<Form.Control.Feedback type="invalid">
							{errors.cvLattes?.message}
						</Form.Control.Feedback>
					</If>
				</Form.Group>
			</Form.Row>
			<Form.Row className="row-custom">
				<Form.Group as={Col} md={2} className="checkbox-lg">
					<Form.Check
						type="checkbox"
						ref={register}
						name="mainTechnician"
						label="Técnico(a) principal?"
					/>
				</Form.Group>
				<Form.Group as={Col} className="checkbox-lg">
					<Form.Check
						type="checkbox"
						ref={register}
						name="active"
						label="Ativo?"
					/>
				</Form.Group>
			</Form.Row>
			<div className="button-position">
				<Button
					title="Cancelar"
					type="button"
					contained={false}
					onClick={() => history.goBack()}
				/>
				<Button
					title="Cadastrar"
					type="submit"
					contained={true}
					isLoading={loading}
				/>
			</div>
		</Form>
	</div>;
}